import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Briefcase, Trophy, BarChart2 } from "lucide-react";
import {
  useCategoria, useCategoriasJugadores,
  useCategoriaStaff, useCategoriaTorneos,
} from "../hooks/useCategorias";
import TablaCategoriaTorneo from "../components/categorias/TablaCategoriaTorneo";

type Tab = "jugadores" | "staff" | "torneos" | "posiciones";

export default function CategoriaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("jugadores");

  const { data: cat, isLoading } = useCategoria(id!);
  const { data: jugadores = [] } = useCategoriasJugadores(id!);
  const { data: staff = [] } = useCategoriaStaff(id!);
  const { data: torneos = [] } = useCategoriaTorneos(id!);

  if (isLoading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;
  if (!cat) return <div className="p-8 text-center text-red-400">Categoría no encontrada</div>;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "jugadores", label: `Jugadores (${jugadores.length})`, icon: <Users className="w-4 h-4" /> },
    { key: "staff", label: `Staff (${staff.length})`, icon: <Briefcase className="w-4 h-4" /> },
    { key: "torneos", label: `Torneos (${torneos.length})`, icon: <Trophy className="w-4 h-4" /> },
    { key: "posiciones", label: "Posiciones", icon: <BarChart2 className="w-4 h-4" /> },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/categorias")} className="text-gray-400 hover:text-gray-700">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          {cat.color_hex && (
            <div className="w-10 h-10 rounded-xl" style={{ backgroundColor: cat.color_hex }} />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{cat.nombre}</h1>
            <p className="text-gray-500 text-sm capitalize">{cat.genero} · {cat.estado}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? "bg-white text-blue-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Jugadores */}
      {tab === "jugadores" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Jugador</th>
                <th className="px-4 py-3 text-left">Posición</th>
                <th className="px-4 py-3 text-center">Dorsal</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {(jugadores as any[]).map((j) => (
                <tr key={j.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{j.nombre} {j.apellido}</td>
                  <td className="px-4 py-3 text-gray-500">{j.posicion_principal}</td>
                  <td className="px-4 py-3 text-center">{j.dorsal ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      j.estado === "activo" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>{j.estado}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => navigate(`/jugadores/${j.id}`)}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Ver perfil
                    </button>
                  </td>
                </tr>
              ))}
              {jugadores.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-gray-400">Sin jugadores asignados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Staff */}
      {tab === "staff" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Staff ID</th>
                <th className="px-4 py-3 text-left">Rol en categoría</th>
                <th className="px-4 py-3 text-left">Desde</th>
              </tr>
            </thead>
            <tbody>
              {(staff as any[]).map((s) => (
                <tr key={s.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-mono text-gray-500 text-xs">{s.staff_id}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{s.rol_en_categoria}</td>
                  <td className="px-4 py-3 text-gray-500">{s.fecha_inicio ?? "—"}</td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr><td colSpan={3} className="py-8 text-center text-gray-400">Sin staff asignado</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: Torneos */}
      {tab === "torneos" && (
        <div className="grid grid-cols-1 gap-4">
          {(torneos as any[]).map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{t.nombre_torneo}</h3>
                <p className="text-sm text-gray-500">Grupo: {t.grupo ?? "—"} · {t.puntos} pts · {t.partidos_jugados} PJ</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  t.estado_torneo === "activo" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}>{t.estado_torneo}</span>
                <button
                  onClick={() => navigate(`/torneos/${t.torneo_id}`)}
                  className="text-blue-600 hover:underline text-xs"
                >
                  Ver torneo
                </button>
              </div>
            </div>
          ))}
          {torneos.length === 0 && (
            <div className="text-center py-12 text-gray-400">No está inscrita en ningún torneo</div>
          )}
        </div>
      )}

      {/* Tab: Posiciones */}
      {tab === "posiciones" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <TablaCategoriaTorneo filas={torneos as any[]} />
        </div>
      )}
    </div>
  );
}
